// 모바일 빠른 매수/매도 등록
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAccounts }                    from '../../api/accounts';
import { fetchStocks }                      from '../../api/stocks';
import { fetchBuyList, fetchNextTurnNo, createBuy } from '../../api/buy';
import { saveSell }                         from '../../api/sell';
import '../styles/mobile.css';

// ── 포맷 헬퍼 ─────────────────────────────────────────────────
const fNum  = v => (v != null && v !== '') ? Number(v).toLocaleString('ko-KR') : '';
const fQty  = v => (v != null && v !== '') ? Number(v).toLocaleString('ko-KR', { maximumFractionDigits: 4 }) : '';
const parse = s  => parseFloat(String(s || '').replace(/,/g, '')) || 0;

export default function QuickTradePage() {
  const { state: rs = {} } = useLocation(); // route state from TurnStatusPage

  // ── 공통 ──────────────────────────────────────────────────
  const [tab,      setTab]      = useState(rs.mode === 'sell' ? 'sell' : 'buy');
  const [accounts, setAccounts] = useState([]);
  const [allStocks,setAllStocks]= useState([]);
  const [toast,    setToast]    = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  useEffect(() => {
    fetchAccounts().then(setAccounts).catch(() => {});
    fetchStocks({ is_active: true }).then(setAllStocks).catch(() => {});
  }, []);

  // ════════════════════════════════════════════════════════════
  // 매수 탭 상태
  // ════════════════════════════════════════════════════════════
  const [buyAcct,     setBuyAcct]     = useState(rs.mode === 'buy' ? (rs.account_name || '') : '');
  const [buyTicker,   setBuyTicker]   = useState(rs.mode === 'buy' ? (rs.ticker      || '') : '');
  const [buyStockName,setBuyStockName]= useState(rs.mode === 'buy' ? (rs.stock_name  || '') : '');
  const [buyDate,     setBuyDate]     = useState('');
  const [buyPrice,    setBuyPrice]    = useState('');
  const [buyQty,      setBuyQty]      = useState('');
  const [buyTurnNo,   setBuyTurnNo]   = useState(null);
  const [buyLoading,  setBuyLoading]  = useState(false);
  const [buySearch,   setBuySearch]   = useState('');
  const [buyListOpen, setBuyListOpen] = useState(false);

  // 차수 자동배정
  useEffect(() => {
    if (!buyAcct || !buyTicker) { setBuyTurnNo(null); return; }
    fetchNextTurnNo(buyAcct, buyTicker).then(setBuyTurnNo).catch(() => setBuyTurnNo(null));
  }, [buyAcct, buyTicker]);

  // 종목 검색 필터
  const buyFiltered = buySearch
    ? allStocks.filter(s =>
        (s.stock_name || '').toLowerCase().includes(buySearch.toLowerCase()) ||
        (s.ticker     || '').toLowerCase().includes(buySearch.toLowerCase()))
    : allStocks;

  const selectBuyStock = (s) => {
    setBuyTicker(s.ticker);
    setBuyStockName(s.stock_name || s.ticker);
    setBuySearch('');
    setBuyListOpen(false);
  };
  const clearBuyStock = () => {
    setBuyTicker(''); setBuyStockName('');
    setBuySearch(''); setBuyTurnNo(null);
  };

  // 매수금액 자동계산
  const buyAmountCalc = buyPrice && buyQty
    ? Math.round(parse(buyPrice) * parse(buyQty) * 100) / 100
    : null;

  const resetBuyForm = () => {
    setBuyAcct(''); setBuyTicker(''); setBuyStockName('');
    setBuyDate(''); setBuyPrice(''); setBuyQty('');
    setBuyTurnNo(null); setBuySearch('');
  };

  const handleBuySubmit = async () => {
    if (!buyAcct)  { showToast('계좌를 선택해주세요.');   return; }
    if (!buyTicker){ showToast('종목을 선택해주세요.');   return; }
    if (!buyPrice) { showToast('매수단가를 입력해주세요.'); return; }
    if (!buyQty)   { showToast('수량을 입력해주세요.');   return; }
    setBuyLoading(true);
    try {
      await createBuy({
        account_name: buyAcct,
        ticker:       buyTicker,
        buy_date:     buyDate || null,
        buy_price:    parse(buyPrice),
        quantity:     parse(buyQty),
      });
      showToast('매수 등록 완료');
      resetBuyForm();
    } catch (e) {
      showToast(e.response?.data?.detail || '등록에 실패했습니다.');
    } finally {
      setBuyLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════
  // 매도 탭 상태
  // ════════════════════════════════════════════════════════════
  const [sellAcct,      setSellAcct]      = useState(rs.mode === 'sell' ? (rs.account_name || '') : '');
  const [sellTicker,    setSellTicker]    = useState(rs.mode === 'sell' ? (rs.ticker        || '') : '');
  const [sellStockName, setSellStockName] = useState(rs.mode === 'sell' ? (rs.stock_name    || '') : '');
  const [sellTurnNo,    setSellTurnNo]    = useState(rs.mode === 'sell' ? (rs.turn_no       ?? null) : null);
  const [sellDate,      setSellDate]      = useState('');
  const [sellPrice,     setSellPrice]     = useState('');
  const [sellQty,       setSellQty]       = useState('');
  const [sellLoading,   setSellLoading]   = useState(false);
  const [sellSearch,    setSellSearch]    = useState('');
  const [sellListOpen,  setSellListOpen]  = useState(false);
  const [unsoldItems,   setUnsoldItems]   = useState([]);
  const [unsoldLoading, setUnsoldLoading] = useState(false);

  // 미매도 목록 조회 (sell_date 기준)
  const loadUnsold = useCallback(async (acct) => {
    if (!acct) { setUnsoldItems([]); return; }
    setUnsoldLoading(true);
    try {
      const data = await fetchBuyList({ account_name: acct });
      setUnsoldItems((data || []).filter(i => !i.sell_date));
    } catch {
      setUnsoldItems([]);
    } finally {
      setUnsoldLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sellAcct) loadUnsold(sellAcct);
  }, [sellAcct, loadUnsold]);

  // route state로 진입 시 초기 로드
  const didInitSell = useRef(false);
  useEffect(() => {
    if (rs.mode === 'sell' && rs.account_name && !didInitSell.current) {
      didInitSell.current = true;
      loadUnsold(rs.account_name);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 미매도 종목 목록 (계좌 내 unique ticker)
  const sellTickers = [
    ...new Map(unsoldItems.map(i => [i.ticker, { ticker: i.ticker, stock_name: i.stock_name }])).values(),
  ];
  const sellFiltered = sellSearch
    ? sellTickers.filter(s =>
        (s.stock_name || '').toLowerCase().includes(sellSearch.toLowerCase()) ||
        (s.ticker     || '').toLowerCase().includes(sellSearch.toLowerCase()))
    : sellTickers;

  // 선택된 ticker의 차수 목록
  const sellTurns = sellTicker ? unsoldItems.filter(i => i.ticker === sellTicker) : [];
  const sellSelectedTurn = sellTurns.find(i => i.turn_no === sellTurnNo) || null;

  const selectSellStock = (s) => {
    setSellTicker(s.ticker);
    setSellStockName(s.stock_name || s.ticker);
    setSellSearch(''); setSellListOpen(false);
    setSellTurnNo(null); setSellQty('');
  };
  const clearSellStock = () => {
    setSellTicker(''); setSellStockName(''); setSellSearch('');
    setSellTurnNo(null); setSellQty('');
  };
  const selectSellTurn = (turn) => {
    setSellTurnNo(turn.turn_no);
    setSellQty(String(turn.quantity != null ? turn.quantity : ''));
  };

  // 매도금액 / 손익 자동계산
  const sellAmountCalc = sellPrice && sellQty
    ? Math.round(parse(sellPrice) * parse(sellQty) * 100) / 100
    : null;
  const sellCostCalc   = sellSelectedTurn?.buy_price && sellQty
    ? sellSelectedTurn.buy_price * parse(sellQty) : null;
  const sellProfitCalc = sellAmountCalc != null && sellCostCalc != null
    ? sellAmountCalc - sellCostCalc : null;

  const resetSellForm = () => {
    setSellAcct(''); setSellTicker(''); setSellStockName('');
    setSellTurnNo(null); setSellDate(''); setSellPrice(''); setSellQty('');
    setSellSearch(''); setUnsoldItems([]);
  };

  const handleSellSubmit = async () => {
    if (!sellAcct)          { showToast('계좌를 선택해주세요.');       return; }
    if (!sellTicker)        { showToast('종목을 선택해주세요.');       return; }
    if (sellTurnNo == null) { showToast('차수를 선택해주세요.');       return; }
    if (!sellPrice)         { showToast('매도단가를 입력해주세요.');   return; }
    if (!sellQty)           { showToast('수량을 입력해주세요.');       return; }
    if (!sellSelectedTurn)  { showToast('차수 정보를 찾을 수 없습니다.'); return; }
    setSellLoading(true);
    try {
      await saveSell({
        buy_id:     sellSelectedTurn.id,
        sell_id:    null,
        sell_date:  sellDate || null,
        sell_price: parse(sellPrice),
        quantity:   parse(sellQty),
      });
      showToast('매도 등록 완료');
      resetSellForm();
    } catch (e) {
      showToast(e.response?.data?.detail || '등록에 실패했습니다.');
    } finally {
      setSellLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════
  // 렌더
  // ════════════════════════════════════════════════════════════
  return (
    <div className="qt-wrap">

      {/* ── 매수/매도 탭 ── */}
      <div className="qt-tabs">
        <button
          className={`qt-tab-btn qt-tab-buy${tab === 'buy' ? ' qt-tab-active' : ''}`}
          onClick={() => setTab('buy')}
        >매수</button>
        <button
          className={`qt-tab-btn qt-tab-sell${tab === 'sell' ? ' qt-tab-active' : ''}`}
          onClick={() => setTab('sell')}
        >매도</button>
      </div>

      {/* ════════════════════════════════════════════
          매수 폼
      ════════════════════════════════════════════ */}
      {tab === 'buy' && (
        <div className="qt-form">

          {/* 계좌 */}
          <div className="qt-field">
            <label className="qt-label">계좌 <span className="qt-req">*</span></label>
            <select className="qt-select" value={buyAcct} onChange={e => setBuyAcct(e.target.value)}>
              <option value="">계좌 선택</option>
              {accounts.map(a => (
                <option key={a.id} value={a.account_name}>{a.account_name}</option>
              ))}
            </select>
          </div>

          {/* 종목 검색 */}
          <div className="qt-field">
            <label className="qt-label">종목 <span className="qt-req">*</span></label>
            {buyTicker ? (
              <div className="qt-selected-stock">
                <span className="qt-sel-name">{buyStockName}</span>
                <span className="qt-sel-ticker">{buyTicker}</span>
                <button className="qt-clear-btn" onClick={clearBuyStock}>×</button>
              </div>
            ) : (
              <div className="qt-search-wrap">
                <input
                  className="qt-input"
                  placeholder="종목명 또는 티커 검색"
                  value={buySearch}
                  onChange={e => { setBuySearch(e.target.value); setBuyListOpen(true); }}
                  onFocus={() => setBuyListOpen(true)}
                  onBlur={() => setTimeout(() => setBuyListOpen(false), 150)}
                />
                {buyListOpen && (
                  <div className="qt-stock-list">
                    {buyFiltered.slice(0, 60).map(s => (
                      <button
                        key={s.ticker}
                        className="qt-stock-item"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => selectBuyStock(s)}
                      >
                        <span className="qt-si-name">{s.stock_name}</span>
                        <span className="qt-si-ticker">{s.ticker}</span>
                      </button>
                    ))}
                    {buyFiltered.length === 0 && (
                      <div className="qt-list-empty">검색 결과가 없습니다.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 차수 (자동배정, 읽기전용) */}
          {buyTicker && (
            <div className="qt-field">
              <label className="qt-label">차수 (자동배정)</label>
              <div className="qt-readonly">{buyTurnNo != null ? `${buyTurnNo}차` : '—'}</div>
            </div>
          )}

          {/* 매수일자 */}
          <div className="qt-field">
            <label className="qt-label">매수일자</label>
            <input
              type="date"
              className="qt-input"
              value={buyDate}
              onChange={e => setBuyDate(e.target.value)}
            />
          </div>

          {/* 매수단가 */}
          <div className="qt-field">
            <label className="qt-label">매수단가 <span className="qt-req">*</span></label>
            <input
              className="qt-input qt-input-num"
              inputMode="decimal"
              placeholder="0"
              value={buyPrice}
              onChange={e => setBuyPrice(e.target.value.replace(/[^0-9.]/g, ''))}
            />
          </div>

          {/* 수량 */}
          <div className="qt-field">
            <label className="qt-label">수량 <span className="qt-req">*</span></label>
            <input
              className="qt-input qt-input-num"
              inputMode="decimal"
              placeholder="0"
              value={buyQty}
              onChange={e => setBuyQty(e.target.value.replace(/[^0-9.]/g, ''))}
            />
          </div>

          {/* 매수금액 (자동계산) */}
          {buyAmountCalc != null && (
            <div className="qt-field">
              <label className="qt-label">매수금액</label>
              <div className="qt-readonly qt-amount">{fNum(buyAmountCalc)}원</div>
            </div>
          )}

          {/* 매수 등록 버튼 */}
          <button className="qt-submit qt-submit-buy" onClick={handleBuySubmit} disabled={buyLoading}>
            {buyLoading ? '등록 중...' : '매수 등록'}
          </button>
        </div>
      )}

      {/* ════════════════════════════════════════════
          매도 폼
      ════════════════════════════════════════════ */}
      {tab === 'sell' && (
        <div className="qt-form">

          {/* 계좌 */}
          <div className="qt-field">
            <label className="qt-label">계좌 <span className="qt-req">*</span></label>
            <select
              className="qt-select"
              value={sellAcct}
              onChange={e => {
                setSellAcct(e.target.value);
                setSellTicker(''); setSellStockName('');
                setSellTurnNo(null); setSellQty('');
              }}
            >
              <option value="">계좌 선택</option>
              {accounts.map(a => (
                <option key={a.id} value={a.account_name}>{a.account_name}</option>
              ))}
            </select>
          </div>

          {/* 종목 (미매도만) */}
          <div className="qt-field">
            <label className="qt-label">종목 <span className="qt-req">*</span></label>
            {!sellAcct ? (
              <div className="qt-placeholder-hint">계좌를 먼저 선택해주세요.</div>
            ) : unsoldLoading ? (
              <div className="qt-readonly">불러오는 중...</div>
            ) : sellTicker ? (
              <div className="qt-selected-stock">
                <span className="qt-sel-name">{sellStockName}</span>
                <span className="qt-sel-ticker">{sellTicker}</span>
                <button className="qt-clear-btn" onClick={clearSellStock}>×</button>
              </div>
            ) : (
              <div className="qt-search-wrap">
                <input
                  className="qt-input"
                  placeholder="종목명 또는 티커 검색"
                  value={sellSearch}
                  onChange={e => { setSellSearch(e.target.value); setSellListOpen(true); }}
                  onFocus={() => setSellListOpen(true)}
                  onBlur={() => setTimeout(() => setSellListOpen(false), 150)}
                />
                {sellListOpen && (
                  <div className="qt-stock-list">
                    {sellTickers.length === 0 ? (
                      <div className="qt-list-empty">미매도 종목이 없습니다.</div>
                    ) : sellFiltered.map(s => (
                      <button
                        key={s.ticker}
                        className="qt-stock-item"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => selectSellStock(s)}
                      >
                        <span className="qt-si-name">{s.stock_name}</span>
                        <span className="qt-si-ticker">{s.ticker}</span>
                      </button>
                    ))}
                    {sellFiltered.length === 0 && sellSearch && (
                      <div className="qt-list-empty">검색 결과가 없습니다.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 차수 선택 */}
          {sellTicker && (
            <div className="qt-field">
              <label className="qt-label">차수 <span className="qt-req">*</span></label>
              <div className="qt-turn-list">
                {sellTurns.map(t => (
                  <button
                    key={t.id}
                    className={`qt-turn-item${sellTurnNo === t.turn_no ? ' qt-turn-sel' : ''}`}
                    onClick={() => selectSellTurn(t)}
                  >
                    <span className="qt-turn-no">{t.turn_no}차</span>
                    <span className="qt-turn-detail">
                      {t.buy_date || '날짜없음'} · {fNum(t.buy_price)}원 · {fQty(t.quantity)}주
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 매수 참고 정보 */}
          {sellSelectedTurn && (
            <div className="qt-ref-box">
              <div className="qt-ref-title">매수 참고 정보</div>
              <div className="qt-ref-grid">
                <span className="qt-ref-k">매수일자</span>
                <span className="qt-ref-v">{sellSelectedTurn.buy_date || '—'}</span>
                <span className="qt-ref-k">매수단가</span>
                <span className="qt-ref-v">{fNum(sellSelectedTurn.buy_price)}원</span>
                <span className="qt-ref-k">수량</span>
                <span className="qt-ref-v">{fQty(sellSelectedTurn.quantity)}주</span>
                <span className="qt-ref-k">매수금액</span>
                <span className="qt-ref-v">
                  {fNum(Math.round((sellSelectedTurn.buy_price || 0) * (sellSelectedTurn.quantity || 0)))}원
                </span>
              </div>
            </div>
          )}

          {/* 매도일자 */}
          <div className="qt-field">
            <label className="qt-label">매도일자</label>
            <input
              type="date"
              className="qt-input"
              value={sellDate}
              onChange={e => setSellDate(e.target.value)}
            />
          </div>

          {/* 매도단가 */}
          <div className="qt-field">
            <label className="qt-label">매도단가 <span className="qt-req">*</span></label>
            <input
              className="qt-input qt-input-num"
              inputMode="decimal"
              placeholder="0"
              value={sellPrice}
              onChange={e => setSellPrice(e.target.value.replace(/[^0-9.]/g, ''))}
            />
          </div>

          {/* 수량 */}
          <div className="qt-field">
            <label className="qt-label">수량 <span className="qt-req">*</span></label>
            <input
              className="qt-input qt-input-num"
              inputMode="decimal"
              placeholder="0"
              value={sellQty}
              onChange={e => setSellQty(e.target.value.replace(/[^0-9.]/g, ''))}
            />
          </div>

          {/* 매도금액 / 손익 */}
          {sellAmountCalc != null && (
            <div className="qt-calc-box">
              <div className="qt-calc-row">
                <span className="qt-calc-k">매도금액</span>
                <span className="qt-calc-v">{fNum(sellAmountCalc)}원</span>
              </div>
              {sellProfitCalc != null && (
                <div className="qt-calc-row">
                  <span className="qt-calc-k">손익</span>
                  <span
                    className="qt-calc-v qt-profit"
                    style={{ color: sellProfitCalc > 0 ? 'var(--mb-pos)' : sellProfitCalc < 0 ? 'var(--mb-neg)' : 'inherit' }}
                  >
                    {sellProfitCalc > 0 ? '+' : ''}{fNum(Math.round(sellProfitCalc))}원
                  </span>
                </div>
              )}
            </div>
          )}

          {/* 매도 등록 버튼 */}
          <button className="qt-submit qt-submit-sell" onClick={handleSellSubmit} disabled={sellLoading}>
            {sellLoading ? '등록 중...' : '매도 등록'}
          </button>
        </div>
      )}

      {toast && <div className="mb-toast">{toast}</div>}
    </div>
  );
}
